import { memo, useCallback, useMemo } from 'react';

import {
  ChakraSimpleGrid,
  ChakraVStack,
  FAIconName,
  InputGroupV2,
  RadioV2,
  TextInputV2,
  Tooltip,
  UniversalSelect,
  convertDashedUSPhoneNumberToInternationalFormat,
} from '@equip.health/ui';
import { validate as validateEmail } from 'email-validator';

import {
  CLASS_MISSING_REQUIRED,
  ContactInfoValue,
} from '~/components/common/inquiry/drawer/types';
import { TextInputV2Type } from '~/lib/constants/inquiry.constants';
import {
  dropdownMenuOptionsMapper,
  getSelectedUniversalSelectOption,
} from '~/lib/util/inquiry.util';
import util from '~/lib/util/util';

import LanguageDropdown from './LanguageDropdown';
import PronounsDropdown from './PronounsDropdown';
import TimezoneDropdown from './TimezoneDropdown';
import { RelationToPatientCategory } from '~/lib/constants/enumeration.constants';
import { useGetRelationsToPatientEnumQuery } from '~/store/services/enumerations';

interface ContactInfoProps {
  id: string;
  contactInfoValue: ContactInfoValue;
  showRequiredFieldErrors: boolean;
  updateContactInfo: (info: Partial<ContactInfoValue>) => void;
}

const ContactInfo = memo(
  ({
    id,
    contactInfoValue,
    showRequiredFieldErrors,
    updateContactInfo,
  }: ContactInfoProps) => {
    const TOP_LEVEL_ID = id;
    const { data: relationsToPatientInContact } =
      useGetRelationsToPatientEnumQuery();
    const familyRelationsToPatientInPatientContact = useMemo<string[]>(
      () =>
        (relationsToPatientInContact || [])
          .filter((x) => x.category === RelationToPatientCategory.FAMILY)
          .map((x) => x.label)
          .sort((a, b) => a.localeCompare(b)),
      [relationsToPatientInContact],
    );

    const {
      cellPhone,
      chosenName,
      email,
      firstName,
      isPrimaryContact,
      isSupport,
      languages,
      lastName,
      pronouns,
      relationToPatient,
      timezone,
    } = contactInfoValue;

    const isEmailValid = useMemo<boolean>(() => validateEmail(email), [email]);
    const isPhoneValid = useMemo<boolean>(
      () =>
        util.validatePhoneNumber(
          convertDashedUSPhoneNumberToInternationalFormat(cellPhone ?? ''),
        ),
      [cellPhone],
    );

    const isEmailRequired = !!isPrimaryContact && !isPhoneValid;
    const isPhoneRequired = !!isPrimaryContact && !isEmailValid;
    const anyFieldFilled =
      firstName?.trim().length > 0 ||
      lastName?.trim().length > 0 ||
      relationToPatient?.trim().length > 0 ||
      cellPhone?.trim().length > 0 ||
      email?.trim().length > 0 ||
      languages?.length > 0 ||
      timezone?.trim().length > 0 ||
      pronouns?.length > 0 ||
      isPrimaryContact === true;

    const showIsInvalidEmail =
      (showRequiredFieldErrors && isEmailRequired && !isEmailValid) ||
      (email && !isEmailValid);
    const showIsInvalidPhone =
      (showRequiredFieldErrors && isPhoneRequired && !isPhoneValid) ||
      (cellPhone && !isPhoneValid);
    const invalidFirstName =
      showRequiredFieldErrors && anyFieldFilled && !firstName?.trim();
    const invalidLastName =
      showRequiredFieldErrors && anyFieldFilled && !lastName?.trim();
    const invalidRelationToPatient =
      showRequiredFieldErrors && anyFieldFilled && !relationToPatient;

    const isMissingRequired =
      showIsInvalidEmail ||
      showIsInvalidPhone ||
      invalidFirstName ||
      invalidLastName ||
      invalidRelationToPatient;

    const relationToPatientOptions = useMemo<UniversalSelectOption[]>(() => {
      return dropdownMenuOptionsMapper(
        familyRelationsToPatientInPatientContact,
      );
    }, [familyRelationsToPatientInPatientContact]);

    const updateStringValue = useCallback(
      (key: keyof ContactInfoValue) =>
        (val: string): void => {
          updateContactInfo({ [key]: val });
        },
      [],
    );

    const updateArrayValue = useCallback(
      (key: keyof ContactInfoValue) =>
        (val: string): void => {
          updateContactInfo({ [key]: val ? [val] : null });
        },
      [],
    );

    return (
      <ChakraVStack
        align="flex-start"
        className={isMissingRequired ? CLASS_MISSING_REQUIRED : undefined}
        gap="24px"
        width="100%"
      >
        <ChakraSimpleGrid columns={3} spacing="24px" width="100%">
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__first-name`}
            isRequired={anyFieldFilled}
            label="First name"
          >
            <TextInputV2
              id={`${TOP_LEVEL_ID}__first-name`}
              isInvalid={invalidFirstName}
              onChange={updateStringValue('firstName')}
              value={firstName}
            />
          </InputGroupV2>
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__last-name`}
            isRequired={anyFieldFilled}
            label="Last name"
          >
            <TextInputV2
              id={`${TOP_LEVEL_ID}__last-name`}
              isInvalid={invalidLastName}
              onChange={updateStringValue('lastName')}
              value={lastName}
            />
          </InputGroupV2>
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__chosen-name`}
            isRequired={false}
            label="Chosen name"
          >
            <TextInputV2
              id={`${TOP_LEVEL_ID}__chosen-name`}
              onChange={updateStringValue('chosenName')}
              value={chosenName}
            />
          </InputGroupV2>
        </ChakraSimpleGrid>
        <ChakraSimpleGrid columns={2} spacing="24px" width="100%">
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__phone-number`}
            isRequired={isPhoneRequired}
            label="Phone number"
          >
            <TextInputV2
              id={`${TOP_LEVEL_ID}__phone-number`}
              isInvalid={showIsInvalidPhone}
              leftIcon={FAIconName.mobileRegular}
              onChange={(value: string) =>
                updateContactInfo({
                  cellPhone: value,
                  isInvalidCellPhone: !util.validatePhoneNumber(
                    convertDashedUSPhoneNumberToInternationalFormat(
                      value ?? '',
                    ),
                  ),
                })
              }
              type={TextInputV2Type.PHONE}
              value={cellPhone}
            />
          </InputGroupV2>
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__email-address`}
            isRequired={isEmailRequired}
            label="Email address"
          >
            <Tooltip
              id={`${TOP_LEVEL_ID}__support-tooltip`}
              isDisabled={!isSupport}
              placement="top"
              tooltipText="This contact has been granted app access. To edit this email address, please go to the patient profile."
            >
              <TextInputV2
                id={`${TOP_LEVEL_ID}__email-address`}
                isDisabled={isSupport}
                isInvalid={showIsInvalidEmail}
                leftIcon={FAIconName.envelopeRegular}
                onChange={(value: string) =>
                  updateContactInfo({
                    email: value,
                    isInvalidEmail: !validateEmail(value),
                  })
                }
                type={TextInputV2Type.EMAIL}
                value={email}
              />
            </Tooltip>
          </InputGroupV2>
          <TimezoneDropdown
            id={TOP_LEVEL_ID}
            onSelect={updateStringValue('timezone')}
            value={timezone}
          />
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__relation-to-patient`}
            isRequired={anyFieldFilled}
            label="Relationship to patient"
          >
            <UniversalSelect
              id={`${TOP_LEVEL_ID}__relation-to-patient`}
              isInvalid={invalidRelationToPatient}
              isSearchable
              isSingleSelect
              onChange={(selected: UniversalSelectOption) =>
                updateContactInfo({
                  relationToPatient: selected?.value,
                })
              }
              options={relationToPatientOptions}
              value={getSelectedUniversalSelectOption(
                relationToPatient,
                relationToPatientOptions,
              )}
            />
          </InputGroupV2>
          <LanguageDropdown
            id={TOP_LEVEL_ID}
            onSelect={updateArrayValue('languages')}
            value={languages?.[0]}
          />
          <PronounsDropdown
            id={TOP_LEVEL_ID}
            onSelectValues={(values) =>
              updateContactInfo({
                pronouns: values ? [...values] : null,
              })
            }
            values={pronouns}
          />
        </ChakraSimpleGrid>
        <RadioV2
          id={`${TOP_LEVEL_ID}__primary-contact`}
          isChecked={isPrimaryContact}
          label="This is the primary contact"
          onChange={(val: boolean) =>
            updateContactInfo({
              isPrimaryContact: val,
            })
          }
        />
      </ChakraVStack>
    );
  },
);
export default ContactInfo;
